<template>
    <div id="biodigestor-monitoring-widget-component">
        <WidgetWrapperComponent :title="$t('biodigestor_monitoring')" id-widget="biodigestor-monitoring-widget">
            <template v-slot:default>
                <DateRangePickerComponent :date_range="date_range" class="mb-4" v-on:update="handleDateRangeUpdate" />
                <v-row>
                    <v-col class="pa-2" cols="12" md="6">
                        <IconTitleValueCardComponent
                            :value="`${totalAdditionalWeight} Kg`"
                            icon="mdi-rotate-3d-variant"
                            title="Poids de biodéchets apporté"
                        />
                    </v-col>
                    <v-col class="pa-2" cols="12" md="6">
                        <IconTitleValueCardComponent
                            :value="`${EvolutionAdditionalWeight} %`"
                            icon="mdi-chart-bell-curve-cumulative"
                            title="Evolution de la production de biodéchets"
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pa-2" cols="12" md="5">
                        <IconTitleValueCardComponent
                            :value="`${totalFlushWeight} Kg`"
                            icon="mdi-trash-can-outline"
                            title="Poids de digestat vidé"
                        />
                    </v-col>
                    <v-col class="pa-2" cols="12" md="7">
                        <IconTitleValueCardComponent icon="mdi-wrench-outline" title="Utilisation du biodigesteur">
                            <template v-slot:value>
                                <div class="d-flex align-center gap-2">
                                    <span class="text-h6 font-weight-bold"> {{ Math.round((activeDays / totalDayPeriod) * 100) }} % </span>
                                    <span class="font-14">{{
                                        `${activeDays} / ${totalDayPeriod} ${$t('date_alert_form_component.days')}`
                                    }}</span>
                                </div>
                            </template>
                        </IconTitleValueCardComponent>
                    </v-col>
                </v-row>
            </template>
        </WidgetWrapperComponent>
    </div>
</template>

<script>
import assetRepository from '@/repositories/AssetRepository'
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DateRangePickerComponent from '@/components/global/picker/DateRangePickerComponent.vue'
import IconTitleValueCardComponent from '@/components/global/containment/IconTitleValueCardComponent.vue'

export default {
    name: 'BiodigestorMonitoringWidgetComponent',
    components: {
        WidgetWrapperComponent,
        DateRangePickerComponent,
        IconTitleValueCardComponent,
    },
    props: {
        asset: {
            type: Object,
            required: true,
        },
        device: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            fetchingOperations: false,
            operations: [],
            totalAdditionalWeight: 0,
            totalFlushWeight: 0,
            activeDays: 0,
            totalDayPeriod: 2,
            EvolutionAdditionalWeight: 0,
            date_range: {
                startDate: this.moment().clone().startOf('day').subtract(1, 'day').toDate(),
                endDate: this.moment().toDate(),
            },
        }
    },
    methods: {
        getTotalAdditionalWeight() {
            let res = 0
            this.operations.forEach((operation) => {
                if (operation.key_operation === 'tank_fill') {
                    res += operation.delta_volume || 0
                }
            })
            this.totalAdditionalWeight = res
        },
        getTotalFlushWeight() {
            let res = 0
            this.operations.forEach((operation) => {
                if (operation.key_operation === 'tank_flush') {
                    res += Math.abs(operation.delta_volume || 0)
                }
            })
            this.totalFlushWeight = res
        },
        getEvolutionAdditionalWeight() {
            const operationsBegin = this.operations.filter(
                (operation) => operation.date_operation_start === this.date_range.startDate && operation.key_operation === 'tank_fill'
            )
            const operationsEnd = this.operations.filter(
                (operation) => operation.date_operation_start === this.date_range.endDate && operation.key_operation === 'tank_fill'
            )
            let addBegin = 0
            operationsBegin.forEach((operation) => (addBegin += operation.delta_volume || 0))
            let addEnd = 0
            operationsEnd.forEach((operation) => (addEnd += operation.delta_volume || 0))
            this.EvolutionAdditionalWeight = addEnd === 0 ? 0 : Math.round(((addEnd - addBegin) / addBegin) * 100)
        },
        getTotalDayUtilisation() {
            const days = new Set()
            this.operations.forEach((operation) => {
                if (operation.key_operation === 'tank_fill') {
                    // Utilisation de moment.js pour formater la date
                    days.add(this.moment(operation.date_operation_start).format('YYYY-MM-DD'))
                }
            })
            // Utilisation de this.date_range.startDate et this.date_range.endDate
            const date1 = this.moment(this.date_range.startDate).startOf('day')
            const date2 = this.moment(this.date_range.endDate).startOf('day')
            const diff = date2.diff(date1, 'days')
            this.totalDayPeriod = diff + 1
            this.activeDays = days.size
        },

        async fetchOperations() {
            this.fetchingOperations = true
            const params = new URLSearchParams({
                date_start: this.moment(this.date_range.startDate).format('YYYY-MM-DD 00:00:00'),
                date_end: this.moment(this.date_range.endDate).format('YYYY-MM-DD 23:59:59'),
                orders: 'date_operation:asc',
                granularity: 'day', // ou la valeur que tu utilises
            })
            return assetRepository
                .getOperationsBetweenDateRange(this.asset.id_asset, params)
                .then((success) => {
                    this.operations = success.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetchingOperations = false
                })
        },

        async handleDateRangeUpdate(date_range) {
            this.date_range = date_range
            await this.fetchOperations()
            this.getTotalAdditionalWeight()
            this.getTotalFlushWeight()
            this.getEvolutionAdditionalWeight()
            this.getTotalDayUtilisation()
        },
    },
    async mounted() {
        await this.fetchOperations()
        this.getTotalAdditionalWeight()
        this.getTotalFlushWeight()
        this.getEvolutionAdditionalWeight()
        this.getTotalDayUtilisation()
    },
}
</script>
